import { WebGLRenderer } from 'three';
/**
 * Manager for managing event display's renderer related functions.
 */
var RendererManager = /** @class */ (function () {
    /**
     * Instantiate the renderer manager by setting up the main renderer.
     * @param elementId ID of the wrapper element.
     */
    function RendererManager(elementId) {
        /** Main renderer to be used by the event display. */
        this.mainRenderer = null;
        /** Overlay renderer for rendering a secondary overlay canvas. */
        this.overlayRenderer = null;
        /** A list of all available/created renderers. */
        this.renderers = [];
        // Main renderer for current browsers
        this.setRenderer(elementId);
    }
    /**
     * Render the overlay.
     * @param scene The event display scene.
     * @param controlsManager Manager for managing controls.
     */
    RendererManager.prototype.render = function (scene, controlsManager) {
        if (this.getOverlayRenderer()) {
            if (!this.getOverlayRenderer().domElement.hidden) {
                var sceneColor = scene.background;
                scene.background = null;
                if (!this.isFixedOverlay()) {
                    this.getOverlayRenderer().render(scene, controlsManager.getOverlayCamera());
                }
                scene.background = sceneColor;
            }
        }
    };
    /**
     * Set the initial renderer.
     * @param elementId ID of the wrapper element.
     */
    RendererManager.prototype.setRenderer = function (elementId) {
        var renderer = new WebGLRenderer();
        this.addRenderer(renderer);
        this.setMainRenderer(renderer);
        this.getMainRenderer().setSize(window.innerWidth, window.innerHeight, false);
        this.getMainRenderer().setPixelRatio(window.devicePixelRatio);
        this.getMainRenderer().domElement.className = 'ui-element';
        this.getMainRenderer().domElement.id = 'three-canvas';
        var canvas = document.getElementById(elementId);
        if (canvas == null) {
            canvas = document.body;
        }
        canvas.appendChild(this.getMainRenderer().domElement);
    };
    // SET/GET
    /**
     * Set the main renderer.
     * @param renderer Three.js WebGLRenderer.
     */
    RendererManager.prototype.setMainRenderer = function (renderer) {
        this.mainRenderer = renderer;
    };
    /**
     * Set the renderer for overlay event display view.
     * @param overlayCanvas Canvas on which the overlay is to be rendered.
     */
    RendererManager.prototype.setOverlayRenderer = function (overlayCanvas) {
        var parameters = {
            canvas: overlayCanvas,
            antialias: false,
            alpha: true
        };
        var overlayRenderer = new WebGLRenderer(parameters);
        this.addRenderer(overlayRenderer);
        this.overlayRenderer = overlayRenderer;
    };
    /**
     * Set the available renderers.
     * @param renderers List of three.js WebGLRenderers.
     */
    RendererManager.prototype.setRenderers = function (renderers) {
        this.renderers = renderers;
    };
    /**
     * Get the main renderer.
     * @returns The main renderer.
     */
    RendererManager.prototype.getMainRenderer = function () {
        return this.mainRenderer;
    };
    /**
     * Get the renderer used for overlay.
     * @returns The overlay renderer.
     */
    RendererManager.prototype.getOverlayRenderer = function () {
        return this.overlayRenderer;
    };
    /**
     * Get all the available renderers.
     * @returns A list of three.js WebGLRenderers
     */
    RendererManager.prototype.getRenderers = function () {
        return this.renderers;
    };
    // FUNCTIONS
    /**
     * Add a renderer to the available renderers list.
     * @param renderer Three.js WebGLRenderer to be added.
     */
    RendererManager.prototype.addRenderer = function (renderer) {
        if (!this.containsObject(renderer, this.renderers)) {
            this.renderers.push(renderer);
        }
    };
    /**
     * Remove a renderer from the available renderers list.
     * @param renderer Three,js WebGLRenderer to be removed.
     */
    RendererManager.prototype.removeControls = function (renderer) {
        var index = this.renderers.indexOf(renderer);
        if (index > -1) {
            this.renderers.splice(index, 1);
        }
    };
    /**
     * Swap any two renderers.
     * @param rendererA Renderer A to be swapped with renderer B.
     * @param rendererB Renderer B to be swapped with renderer A.
     */
    RendererManager.prototype.swapRenderers = function (rendererA, rendererB) {
        var temp = rendererA;
        rendererA = rendererB;
        rendererB = temp;
    };
    /**
     * Check if the list of available renderers contains a renderer.
     * @param obj The renderer to be checked for containment.
     * @param list List of available renderers.
     * @returns If the list contains the renderer or not.
     */
    RendererManager.prototype.containsObject = function (obj, list) {
        for (var _i = 0, list_1 = list; _i < list_1.length; _i++) {
            var object = list_1[_i];
            if (object === obj) {
                return true;
            }
        }
        return false;
    };
    /**
     * Set if local clipping is to be enabled or disabled for all the available renderers.
     * @param value If the local clipping is to be enabled or disabled.
     */
    RendererManager.prototype.setLocalClippingEnabled = function (value) {
        for (var _i = 0, _a = this.renderers; _i < _a.length; _i++) {
            var renderer = _a[_i];
            renderer.localClippingEnabled = value;
        }
    };
    /**
     * Get if the local clipping for the first renderer is enabled or disabled.
     * @returns If the renderer local clipping is enabled or disabled.
     */
    RendererManager.prototype.getLocalClipping = function () {
        if (this.renderers.length > 0) {
            return this.renderers[0].localClippingEnabled;
        }
    };
    /**
     * Check if the overlay is fixed or not.
     * @returns If the overlay is fixed or not.
     */
    RendererManager.prototype.isFixedOverlay = function () {
        return this.fixedOverlay;
    };
    /**
     * Set if the overlay is to be fixed or not.
     * @param value If the overlay is to be fixed or not.
     */
    RendererManager.prototype.setFixOverlay = function (value) {
        this.fixedOverlay = value;
    };
    return RendererManager;
}());
export { RendererManager };
//# sourceMappingURL=renderer-manager.js.map